import { useDynamicConfig } from "features/Statsig";
import classNames from "classnames";
import {
  GLOBAL_RESULT_FORMAT_KEY,
  ResultFormat,
} from "components/Button/ResultFormatButton/ResultFormatButton";
import { useNewThread } from "features/NewThreadButton/hooks/useNewThread/useNewThread";
import { useNewThreadEvents } from "features/NewThreadButton/hooks/useNewThreadEvents";
import { DeepSearchAnnouncementBanner } from "features/Thread/components/DeepSearchAnnouncementBanner";
import { useCanCreateNewThreadInteraction } from "features/Thread/hooks/useCanCreateNewThreadInteraction";
import { useDeepSearchAnnouncementBanner } from "features/Thread/hooks/useDeepSearchAnnouncementBanner";
import { ExampleQueriesCategory, SearchMode } from "helpers/api";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useIsScrolledDown from "hooks/useIsScrollledDown";
import useLabels from "hooks/useLabels";
import useRequireSignIn from "hooks/useRequireSignIn";
import { useSearch } from "hooks/useSearch";
import useSearchMode from "hooks/useSearchMode";
import { useAppSelector } from "hooks/useStore";
import { selectSources } from "store/slices/sources";
import { useRouter } from "next/router";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Autosuggest from "react-autosuggest";
import { toast } from "react-toastify";
import { PremiumFeatureSecondaryButton } from "store/slices/subscription";
import ExampleQueryButtons from "./components/ExampleQueryButtons";
import { SearchInputField } from "./components/SearchInputField";
import useCheckSearchCredits from "./hooks/useCheckSearchCredits";
import { useSearchSuggestions } from "./hooks/useSearchSuggestions";
import { FeatureFlag } from "features/Statsig/constants";
import { useFeatureGate } from "features/Statsig";

type SearchInputProps = {
  exampleQueries?: ExampleQueriesCategory[];
};

// Deep Search example queries to show when "Try it" is clicked
const DEEP_SEARCH_BACKUP_EXAMPLE_QUERIES = [
  "What are the health effects of intermittent fasting?",
  "How does artificial intelligence impact education?",
  "What is the current state of renewable energy research?",
  "How effective is mindfulness meditation for anxiety?",
  "What are the latest developments in cancer immunotherapy?",
];
// The number of random example queries to show when "Try it" is clicked
const NUM_DEEP_SEARCH_EXAMPLE_QUERIES_TO_INCLUDE = 5;
export interface SearchInputRef {
  triggerDeepSearchSuggestions: () => void;
}

/**
 * @component SearchInput
 * @description The search input rendered on the home page.
 */
const SearchInput = forwardRef<SearchInputRef, SearchInputProps>(
  ({ exampleQueries = [] }, ref) => {
    const router = useRouter();
    const [generalLabels] = useLabels("general");
    const { redirectIfNotSignedIn } = useRequireSignIn();

    const isDataSourceSelectorEnabled = useFeatureGate(
      FeatureFlag.DATA_SOURCE_SELECTOR
    ).value;

    const deepSearchAnnouncementBannerDynamicConfig = useDynamicConfig(
      "announcement_banner_image_link"
    );
    const deepSearchExampleQueries =
      (deepSearchAnnouncementBannerDynamicConfig?.value?.queries as string[]) ||
      DEEP_SEARCH_BACKUP_EXAMPLE_QUERIES;

    const [searchInputValue, setSearchInputValue] = useState<string>("");
    const [selectedExampleQueries, setSelectedExampleQueries] = useState<
      string[]
    >([]);
    const isSearching = useAppSelector((state) => state.search.isSearching);
    const focusSearchInput = useAppSelector(
      (state) => state.search.focusSearchInput
    );
    const sources = useAppSelector(selectSources);
    const { focusSearchInputBox } = useSearch();
    const [focused, setFocused] = React.useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const {
      suggestions,
      setSelectedItemIndex,
      onSuggestionsRequested,
      onSuggestionsCleared,
      handleKeyboardNavigation,
    } = useSearchSuggestions();

    const { currentSearchMode, changeSearchMode } = useSearchMode();
    const { createNewThread } = useNewThread();
    const { redirectIfOutOfCredits } = useCheckSearchCredits();
    const checkCanCreate = useCanCreateNewThreadInteraction();
    const { trackSearch } = useNewThreadEvents();
    const { isDeepSearchBannerHidden } = useDeepSearchAnnouncementBanner();

    const isScrolledDown = useIsScrolledDown();

    const isHomePage = router.pathname === "/" || router.pathname === "/search";

    // Automatically set search mode to PRO_ANALYSIS when sources are attached
    useEffect(() => {
      if (sources && sources.length > 0) {
        changeSearchMode(SearchMode.PRO_ANALYSIS);
      }
    }, [sources, changeSearchMode]);

    const submittingRef = useRef(false);

    // Expose methods through ref
    useImperativeHandle(ref, () => ({
      triggerDeepSearchSuggestions: () => {
        setSelectedExampleQueries(
          [...deepSearchExampleQueries]
            .sort(() => Math.random() - 0.5)
            .slice(0, NUM_DEEP_SEARCH_EXAMPLE_QUERIES_TO_INCLUDE)
        );
        setSearchInputValue("");
        inputRef.current?.focus();
        setFocused(true);
      },
    }));

    const onSearch = useCallback(
      (searchValue: string, searchMode?: SearchMode) => {
        const mode = searchMode || currentSearchMode;

        if (submittingRef.current) return;
        submittingRef.current = true;
        setTimeout(() => {
          submittingRef.current = false;
        }, 500);

        const [canCreate, message] = checkCanCreate(mode);
        if (!canCreate) {
          toast.error(message);
          return;
        }

        const isOutOfCredits = redirectIfOutOfCredits(
          mode,
          MixpanelActionSource.MainSearchBar,
          {
            button: PremiumFeatureSecondaryButton.CREATE_NEW_THREAD,
            userMessage: searchValue,
          }
        );
        if (isOutOfCredits) return;

        inputRef.current?.blur();

        trackSearch(searchValue, MixpanelActionSource.MainSearchBar);

        createNewThread({
          userMessage: searchValue,
          searchMode: mode,
          actionSource: MixpanelActionSource.MainSearchBar,
          sources: sources.length > 0 ? sources : undefined,
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        createNewThread,
        inputRef,
        redirectIfOutOfCredits,
        trackSearch,
        currentSearchMode,
        sources,
      ]
    );

    const onSubmit = useCallback(() => {
      onSearch(searchInputValue);
    }, [onSearch, searchInputValue]);

    const onChangeSearchInput = useCallback((e: any) => {
      setSearchInputValue(e.target.value);
    }, []);

    const onSuggestionSelected = useCallback(
      (_: any, { suggestion }: { suggestion: string }) => {
        // Check if this is a Deep Search suggestion
        const isDeepSearchQuery =
          selectedExampleQueries.length > 0 &&
          exampleQueries
            .find((cat) => cat.key === "research_questions")
            ?.queries.includes(suggestion);

        if (isDeepSearchQuery) {
          // Check authentication for Deep Search
          const isSignedIn = redirectIfNotSignedIn(router.asPath);
          if (!isSignedIn) {
            return; // User is being redirected to sign-up
          }

          // Switch to PRO_RESEARCH mode
          changeSearchMode(SearchMode.PRO_RESEARCH);
          localStorage.setItem(
            GLOBAL_RESULT_FORMAT_KEY,
            JSON.stringify(ResultFormat.COMPACT)
          );
        }

        inputRef.current?.blur();
        setSearchInputValue(suggestion);
        onSearch(
          suggestion,
          isDeepSearchQuery ? SearchMode.PRO_RESEARCH : currentSearchMode
        );
        setSelectedItemIndex(-1);
      },
      [
        onSearch,
        setSelectedItemIndex,
        selectedExampleQueries,
        exampleQueries,
        redirectIfNotSignedIn,
        changeSearchMode,
        router,
        currentSearchMode,
      ]
    );

    useEffect(() => {
      if (router?.query?.q) {
        setSearchInputValue(router?.query?.q as string);
      }
    }, [router?.query?.q]);

    useEffect(() => {
      if (isHomePage) {
        setFocused(true);
        inputRef.current?.focus();
      }
    }, [isHomePage]);

    useEffect(() => {
      if (focusSearchInput) {
        setFocused(true);
        inputRef.current?.focus();
      }
    }, [focusSearchInput]);

    const handleFocus = () => {
      setFocused(true);
    };

    const handleBlur = () => {
      setFocused(false);
      focusSearchInputBox(false);
      setSelectedExampleQueries([]);
    };

    const enterKeyHintStaticallyTypedToPassTypeCheck: "go" = "go";
    const inputProps = {
      value: searchInputValue || "",
      onChange: onChangeSearchInput,
      ref: inputRef,
      type: "text",
      enterKeyHint: enterKeyHintStaticallyTypedToPassTypeCheck,
      "data-testid": "search-input",
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) =>
        handleKeyboardNavigation(e, onSubmit),
    };

    const handleExampleCategoryClick = useCallback(
      (queries: string[]) => {
        setSelectedExampleQueries(queries);
        setSearchInputValue("");
        if (queries.length > 0) {
          onSuggestionsRequested({ value: queries[0] });
        }
        inputRef.current?.focus();
      },

      [onSuggestionsRequested]
    );

    const handleTryDeepSearch = () => {
      // Trigger deep search suggestions in the SearchInput component
      if (ref && "current" in ref && ref.current) {
        ref.current.triggerDeepSearchSuggestions();
      }
    };

    const allSuggestions = useMemo(() => {
      return inputProps.value ? suggestions : selectedExampleQueries;
    }, [selectedExampleQueries, suggestions, inputProps.value]);

    return (
      <form
        data-testid="search-input-form"
        className="relative mx-auto leading"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Autosuggest
          suggestions={allSuggestions}
          onSuggestionsFetchRequested={onSuggestionsRequested}
          onSuggestionsClearRequested={onSuggestionsCleared}
          getSuggestionValue={(suggestion: string) => suggestion}
          renderSuggestion={(suggestion: string, { isHighlighted }) => (
            <span
              className={classNames("flex rounded-xl", {
                "bg-bg-faint": isHighlighted,
              })}
            >
              <span className="w-3"></span>
              <span
                className="flex flex-1 justify-between items-center py-3 border-b"
                id="suggestion-text"
              >
                <span className="text-fg-base">{suggestion}</span>
                <span className="w-5 h-6">
                  {isHighlighted ? (
                    <i className="text-xl icon-arrow-right text-fg-muted" />
                  ) : null}
                </span>
              </span>
              <span className="w-3"></span>
            </span>
          )}
          onSuggestionSelected={onSuggestionSelected}
          shouldRenderSuggestions={(value, reason) => true}
          inputProps={inputProps}
          renderInputComponent={(inputProps) => {
            // @ts-ignore - key is added by react-autosuggest but not included in types
            const { key, ...otherProps } = inputProps;
            return (
              <SearchInputField
                key={key}
                {...otherProps}
                isHomePage={isHomePage}
                isScrolledDown={isScrolledDown}
                focused={focused}
                isSearching={isSearching}
                showDataSourceSelector={isDataSourceSelectorEnabled}
                onClear={() => {
                  setSearchInputValue("");
                  setSelectedExampleQueries([]);
                }}
                onSubmit={onSubmit}
              />
            );
          }}
          renderSuggestionsContainer={({ containerProps, children }) => (
            <div
              {...containerProps}
              key={containerProps.key}
              className={`${containerProps.className} ${
                isHomePage ? "border-t" : "border-t-0"
              }`}
            >
              {children}
            </div>
          )}
        />

        {isDeepSearchBannerHidden ? (
          <ExampleQueryButtons
            exampleQueries={exampleQueries}
            onCategoryClick={handleExampleCategoryClick}
          />
        ) : (
          <div className="mt-4">
            <DeepSearchAnnouncementBanner onTryIt={handleTryDeepSearch} />
          </div>
        )}
      </form>
    );
  }
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
